'use client';

import { c as _c } from "react/compiler-runtime";
import { ReactNode, useId } from 'react';
import clsx from 'clsx';
import { useFormatter, useTranslations } from 'next-intl';
import { useCurrency } from '@/hooks/useCurrency';
import { RadioLinkItem } from '@/components/Filters/FilterContent/RadioLinkItem';
type ShippingMethodProps = {
  name: string;
  minDays: number;
  maxDays: number;
  price: number;
  checked: boolean;
  onChange: () => void;
  amountToFreeShipping?: number;
};
type VolumeDiscountShippingProps = {
  shippingData: ShippingOption[] | undefined;
  isFetching?: boolean;
  isOrderEmpty?: boolean;
  amountToFreeShipping?: number;
  selectedShippingTypeId?: number;
  onShippingSelect: (shippingTypeId: number) => void;
};
type BadgeProps = {
  variant?: 'neutral' | 'green';
  children: ReactNode;
  classNames?: string;
};
function Badge(t0) {
  const $ = _c(6);
  const {
    variant: t1,
    children,
    classNames
  } = t0;
  const variant = t1 === undefined ? "neutral" : t1;
  const t2 = variant === "green" ? "bg-green-100 text-green-900" : "bg-neutral-100 text-neutral-700";
  let t3;
  if ($[0] !== classNames || $[1] !== t2) {
    t3 = clsx("rounded-full px-2 py-0.5 text-xs leading-none font-bold", t2, classNames);
    $[0] = classNames;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== t3) {
    t4 = <span className={t3}>{children}</span>;
    $[3] = children;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
export const formatShippingDays = (minDays: number, maxDays: number) => minDays === maxDays ? String(minDays) : `${minDays} - ${maxDays}`;
function ShippingMethod(t0) {
  const $ = _c(26);
  const {
    name,
    minDays,
    maxDays,
    price,
    checked,
    onChange,
    amountToFreeShipping
  } = t0;
  const {
    data: currency
  } = useCurrency();
  const formatter = useFormatter();
  const t = useTranslations("volumeDiscountCalculator.shipping");
  let t1;
  if ($[0] !== maxDays || $[1] !== minDays) {
    t1 = formatShippingDays(minDays, maxDays);
    $[0] = maxDays;
    $[1] = minDays;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const shippingDays = t1;
  let t2;
  if ($[3] !== t) {
    t2 = t("shippingDays");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== shippingDays || $[6] !== t2) {
    t3 = <span className="shrink-1 text-sm font-bold">{shippingDays} {t2}</span>;
    $[5] = shippingDays;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== amountToFreeShipping || $[9] !== currency || $[10] !== formatter || $[11] !== price || $[12] !== t) {
    t4 = price === 0 ? <Badge variant="green">{t("free")}</Badge> : <><Badge>{formatter.number(price, {
          style: "currency",
          currency
        })}</Badge>{amountToFreeShipping !== undefined && amountToFreeShipping > 0 && currency && <Badge variant="green" classNames="flex-1 py-1 md:py-0.5">{t("freeShippingThreshold", {
          amount: formatter.number(amountToFreeShipping, {
            style: "currency",
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          })
        })}</Badge>}</>;
    $[8] = amountToFreeShipping;
    $[9] = currency;
    $[10] = formatter;
    $[11] = price;
    $[12] = t;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = <span className="flex items-center gap-2 whitespace-normal">{t3}{t4}</span>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== checked || $[18] !== onChange || $[19] !== t5) {
    t6 = <RadioLinkItem checked={checked} onChange={onChange}>{t5}</RadioLinkItem>;
    $[17] = checked;
    $[18] = onChange;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== name) {
    t7 = <span className="pl-7 text-xs font-medium text-neutral-700">{name}</span>;
    $[21] = name;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t6 || $[24] !== t7) {
    t8 = <div className="flex flex-col gap-0.5">{t6}{t7}</div>;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}
const SKELETON_ROWS = 2;
function ShippingMethodSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex animate-pulse flex-col gap-1.5 motion-reduce:animate-none" aria-hidden="true"><div className="h-5 w-72 max-w-full bg-neutral-100" /><div className="ml-7 h-3 w-20 bg-neutral-100" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export default function VolumeDiscountShipping(t0) {
  const $ = _c(19);
  const {
    shippingData,
    isFetching,
    isOrderEmpty,
    amountToFreeShipping,
    selectedShippingTypeId,
    onShippingSelect
  } = t0;
  const showQuantityHint = isOrderEmpty && shippingData === undefined;
  const isInitialLoad = shippingData === undefined && isFetching;
  const isRefetching = shippingData !== undefined && isFetching;
  const t = useTranslations("volumeDiscountCalculator.shipping");
  const id = useId();
  let t1;
  if ($[0] !== t) {
    t1 = t("shippingTitle");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== id || $[3] !== t1) {
    t2 = <h3 id={id} className="font-display mb-4 text-xs font-black tracking-[0.1em] text-neutral-800 uppercase">{t1}</h3>;
    $[2] = id;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== amountToFreeShipping || $[6] !== id || $[7] !== isFetching || $[8] !== isInitialLoad || $[9] !== isRefetching || $[10] !== onShippingSelect || $[11] !== selectedShippingTypeId || $[12] !== shippingData || $[13] !== showQuantityHint || $[14] !== t) {
    t3 = showQuantityHint ? <p className="text-sm text-neutral-700">{t("selectQuantity")}</p> : <div role="radiogroup" aria-labelledby={id} aria-busy={isFetching} className={clsx("flex flex-col gap-3", {
      "opacity-50": isRefetching
    })}>{isInitialLoad && Array.from({
        length: SKELETON_ROWS
      }, _temp)}{shippingData?.map(t4 => {
        const {
          shippingTypeId,
          title,
          minDays,
          maxDays,
          price,
          freeShippingThreshold
        } = t4;
        return <ShippingMethod key={shippingTypeId} name={title} minDays={minDays} maxDays={maxDays} price={price} checked={selectedShippingTypeId === shippingTypeId} onChange={() => onShippingSelect(shippingTypeId)} amountToFreeShipping={freeShippingThreshold !== undefined ? amountToFreeShipping : undefined} />;
      })}</div>;
    $[5] = amountToFreeShipping;
    $[6] = id;
    $[7] = isFetching;
    $[8] = isInitialLoad;
    $[9] = isRefetching;
    $[10] = onShippingSelect;
    $[11] = selectedShippingTypeId;
    $[12] = shippingData;
    $[13] = showQuantityHint;
    $[14] = t;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== t2 || $[17] !== t3) {
    t4 = <div className="mt-8 pr-6 md:pr-9">{t2}{t3}</div>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
}
function _temp(_, i) {
  return <ShippingMethodSkeleton key={i} />;
}